import { useEffect, useRef, useState } from 'react'
import type { DocumentLock, DocumentLockKeyEnum } from '@ztechapps/common.contract'
import { useMutation } from '../useMutation/index'
import { gql } from '@apollo/client'
import type { OnDataOptions } from '@apollo/client/react/types/types'
import { useSubscription, type UseSubscriptionProps } from '../useSubscription'
import { useError } from '../useError'
import { usePageLifecycle } from '../usePageLifeCycle'
import { toDate } from '@ztechapps/common.utils'

const documentLockFragment = gql`
  fragment documentLockFragment on DocumentLock {
    _id
    key
    value
    isLocked
    updatedAt
    user {
      _id
      profile {
        _id
        name
        email
        picture
      }
    }
  }
`

const setDocumentLockQuery = gql`
  ${documentLockFragment}
  mutation setDocumentLock($input: JSON!) {
    setDocumentLock(input: $input) {
      ...documentLockFragment
    }
  }
`

const stealDocumentLockQuery = gql`
  ${documentLockFragment}
  mutation stealDocumentLock($input: JSON!) {
    stealDocumentLock(input: $input) {
      ...documentLockFragment
    }
  }
`

const removeDocumentLockQuery = gql`
  ${documentLockFragment}
  mutation removeDocumentLock($input: JSON!) {
    removeDocumentLock(input: $input) {
      ...documentLockFragment
    }
  }
`

const lockedDocumentSubscription = gql`
  ${documentLockFragment}
  subscription lockedDocument($filter: JSON!) {
    lockedDocument(filter: $filter) {
      ...documentLockFragment
    }
  }
`

const unLockedDocumentSubscription = gql`
  ${documentLockFragment}
  subscription unLockedDocument($filter: JSON!) {
    unLockedDocument(filter: $filter) {
      ...documentLockFragment
    }
  }
`

export type UseDocumentLockResult = {
  setLock: () => Promise<void>
  removeLock: () => any
  stealLock: () => Promise<void>
  documentLock: DocumentLock
  isLocked: boolean
}

export type UseDocumentLockProps = {
  key: DocumentLockKeyEnum
  value: string
  initialValue?: DocumentLock
  onUnlocked?: (documentLock: DocumentLock) => void
  onLocked?: (documentLock: DocumentLock) => void
}

const useLockedDocumentSubscription = (
  { query, onData, filter = {} } = {} as UseSubscriptionProps<{ lockedDocument: DocumentLock }>
) => {
  query ??= lockedDocumentSubscription
  return useSubscription({ query, filter, onData })
}

const useUnLockedDocumentSubscription = (
  { query, onData, filter = {} } = {} as UseSubscriptionProps<{ unLockedDocument: DocumentLock }>
) => {
  query ??= unLockedDocumentSubscription
  return useSubscription({ query, filter, onData })
}

export const useDocumentLock = ({
  key,
  value,
  initialValue,
  onLocked,
  onUnlocked,
}: UseDocumentLockProps): UseDocumentLockResult => {
  const lastTimeStamp = useRef<number>(null)
  const [setDocumentLockMutation] = useMutation<{ setDocumentLock: DocumentLock }>({ query: setDocumentLockQuery })
  const [removeDocumentLockMutation] = useMutation<{ removeDocumentLock: DocumentLock }>({
    query: removeDocumentLockQuery,
  })
  const [stealDocumentLockMutation] = useMutation<{ stealDocumentLock: DocumentLock }>({
    query: stealDocumentLockQuery,
  })
  const { onError } = useError()
  const [documentLock, setDocumentLock] = useState<DocumentLock>(initialValue)

  const onDocumentLock = (documentLock: DocumentLock) => {
    const currentTimeStamp = documentLock?.updatedAt ? toDate(documentLock?.updatedAt).unix() : null

    if (lastTimeStamp.current == null || currentTimeStamp >= lastTimeStamp.current) {
      lastTimeStamp.current = currentTimeStamp
      setDocumentLock(documentLock)
    }
  }

  //subscribe on the locked of document
  const onLockedDocumentData = (onDataOptions: OnDataOptions<{ lockedDocument: DocumentLock }>) => {
    const documentLock = onDataOptions?.data?.data?.lockedDocument
    onDocumentLock(documentLock)
    onLocked?.(documentLock)
  }
  useLockedDocumentSubscription({ onData: onLockedDocumentData, filter: { value, key } })

  //subscribe on the unlocked of document
  const onUnLockedDocumentData = (onDataOptions: OnDataOptions<{ unLockedDocument: DocumentLock }>) => {
    //if document is not locked, try to lock it
    setLock()
    onUnlocked?.(onDataOptions?.data?.data?.unLockedDocument)
  }
  useUnLockedDocumentSubscription({ onData: onUnLockedDocumentData, filter: { value, key } })

  const setLock = async () => {
    //initial locking,
    //this will trigger the locked document event
    const { data } = await setDocumentLockMutation({ input: { key, value } })

    onDocumentLock(data?.setDocumentLock)
  }

  const removeLock = async () => {
    //this will trigger the unlock document event
    await removeDocumentLockMutation({ input: { key, value } })

    onDocumentLock(null)
  }

  const stealLock = async () => {
    try {
      //steal the lock, this will trigger the locked document event
      const { data } = await stealDocumentLockMutation({ input: { key, value } })

      onDocumentLock(data?.stealDocumentLock)
    } catch (e) {
      onError(e)
    }
  }

  //automatically call the set lock if there is a documet Id (value)
  useEffect(() => {
    //ignore the setting of lock if there is no value
    if (!value) return

    setLock() //just fire and forget it
    // setLock intentionally excluded: it is redefined every render (not memoized) but always reads the
    // current key/value from this same render's closure, so this effect firing only on `value` already
    // locks with the latest key. Adding setLock would re-run this on every render, calling the
    // set-lock mutation repeatedly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const onPageLoad = () => {
    //ignore the setting of lock if there is no value
    if (!value) return

    setLock()
  }
  const onPageUnload = () => {
    removeLock()
    lastTimeStamp.current = null
  }
  usePageLifecycle(onPageLoad, onPageUnload)

  //remove the document lock

  return { setLock, removeLock, documentLock, isLocked: documentLock?.isLocked ?? false, stealLock }
}
